import React, { useId } from 'react';
import styled, { keyframes } from 'styled-components';
import { LuX } from 'react-icons/lu';
import { useModalBlocker } from '../contexts/ModalGateContext';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const overlayFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  animation: ${overlayFade} 0.3s ease-out;

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const ModalContent = styled.div`
  background: white;
  padding: 32px;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 580px;
  position: relative;
  margin: auto;
  flex-shrink: 0;
  animation: ${fadeIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: visible;

  @media (max-width: 480px) {
    padding: 24px;
    margin: 0 auto;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: #f1f5f9;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #e2e8f0;
    transform: rotate(90deg);
    color: #1e293b;
  }
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e3a8a;
  margin-top: 0;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
`;

const ModalBody = styled.div`
  font-size: 15px;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;
`;

const Modal = ({
  isOpen,
  onClose,
  children,
  closeOnOverlayClick = true,
  showCloseButton = true,
  gateBlocker = true,
}) => {
  const gateId = useId();
  useModalBlocker(gateId, Boolean(gateBlocker && isOpen));

  if (!isOpen) {
    return null;
  }

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        {showCloseButton && (
          <CloseButton onClick={onClose}>
            <LuX size={20} />
          </CloseButton>
        )}
        {children}
      </ModalContent>
    </ModalOverlay>
  );
};

export { Modal, ModalTitle, ModalBody, ModalFooter };